'use memo';

import { useCallback, useMemo } from 'react';
import { defaultNS, useTranslation } from '@/lib/i18n';

/**
 * API の ContractTypeEnum に対応するラベルマッピング。
 * Backend API は全契約タイプ（OFFICE_RENTAL / OTHER / BOARD_MEMBER 含む）をアラート対象にするため、
 * ここでも全種別のラベルを用意する。
 *
 * `BOARD_MEMBER` は api-contract pin が追いつくまでの間も表示できるよう、
 * OpenAPI 型ユニオン外でも受け付ける。
 */
const TYPE_LABEL_KEY = {
  EMPLOYMENT: 'employment',
  SERVICE: 'service',
  LEASE: 'leaseAsset',
  PURCHASE: 'purchase',
  SAAS_SUBSCRIPTION: 'subscription',
  OFFICE_RENTAL: 'officeRental',
  OTHER: 'other',
  BOARD_MEMBER: 'boardMember'
} as const satisfies Record<string, string>;
type KnownContractAlertType = keyof typeof TYPE_LABEL_KEY;
const CONTRACT_TYPE_FILTER_VALUES = Object.keys(TYPE_LABEL_KEY) as KnownContractAlertType[];

/** 契約種別ラベルの解決と、フィルタ用 options をまとめて提供する。 */
export function useContractTypeLabels() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.contract.list.tabs'
  });
  const labelFor = useCallback((type: string): string => {
    const key = TYPE_LABEL_KEY[type as KnownContractAlertType];
    return key ? t(key) : type;
  }, [t]);
  const options = useMemo(() => CONTRACT_TYPE_FILTER_VALUES.map(type => ({
    id: type,
    value: type,
    label: labelFor(type)
  })), [labelFor]);
  return {
    labelFor,
    options
  };
}